<template lang="html">
    <div :class="['chart-legend', { 'vertical': vertical }]">
        <div
            v-for="(item, index) in processedItems"
            :key="index"
            :class="['legend-item', { 'transparent': hiddenItems.includes(item.originalIndex) }]"
            @click="onItemClick(item.originalIndex)"
        >
            <div
                v-if="isHeader(item) && vertical"
                class="header"
            >
                {{ item.legendLabel }}
            </div>
            <div
                v-else
                class="row"
            >
                <div class="legend-item-label">
                    {{ item.legendLabel }}
                </div>
                <div
                    v-if="isLine(item) && item.legendValue"
                    class="value"
                >
                    <span v-if="typeof item.legendValue === 'number'">
                        {{ item.legendValue | numFormat('0,0.00') }}{{ measurementUnit }}
                    </span>
                    <span v-else>
                        {{ item.legendValue }}
                    </span>
                </div>
                <div
                    v-if="isLine(item)"
                    class="visual-container"
                >
                    <div
                        :class="['visual', 'line', { 'solid': isSolidLine(item), 'dashed': isDashedLine(item) }]"
                        :style="{ borderColor: item.color }"
                    />
                </div>
                <div
                    v-else-if="isCircle(item)"
                    class="visual-container"
                >
                    <div
                        :class="['visual', 'circle', { 'border': item.borderColor }]"
                        :style="{ backgroundColor: item.color, borderColor: item.borderColor }"
                    />
                </div>
                <div
                    v-else
                    class="visual-container"
                >
                    <div
                        :class="['visual', 'color-box', { 'border': item.borderColor }]"
                        :style="{ backgroundColor: item.color, borderColor: item.borderColor }"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        /**
         * Items to show in the legend
         */
        items: {
            type: Array,
            required: true,
        },
        /**
         * The indexes of any items that are hidden on the chart. With this list, the legend knows which items to grey
         * out/strikethrough.
         */
        hiddenItems: {
            type: Array,
            required: false,
            default: () => [],
        },
        /**
         * If true, the legend will be displayed vertically
         */
        vertical: {
            type: Boolean,
            required: false,
            default: false,
        },
        /**
         * The measurement unit of the data
         */
        measurementUnit: {
            type: String,
            required: false,
            default: '',
        },
    },
    computed: {
        /**
         * Add the original index of the items to them and sort them by their set labelOrder.
         */
        processedItems() {
            const newItems = this.items.map((item, index) => ({
                ...item,
                originalIndex: index,
            }));

            newItems.sort((a, b) => a.labelOrder - b.labelOrder);
            return newItems;
        },
    },
    methods: {
        /**
         * When an item is clicked, emit an event to let the parent component know.
         * * @param {number} index - the index of the item that was clicked
         */
        onItemClick(index) {
            this.$emit('item-clicked', index);
        },
        /**
         * Determines if the given item is a line.
         * @param {Object} item
         */
        isLine(item) {
            return this.isSolidLine(item) || this.isDashedLine(item);
        },
        /**
         * Determines if the given item is a solid line.
         * @param {Object} item
         */
        isSolidLine(item) {
            return item.legendType === 'solidLine';
        },
        /**
         * Determines if the given item is a dashed line.
         * @param {Object} item
         */
        isDashedLine(item) {
            return item.legendType === 'dashedLine';
        },
        /**
         * Determines if the given item is a circle.
         * @param {Object} item
         */
        isCircle(item) {
            return item.legendType === 'circle';
        },
        /**
         * Determines if the given item is a header.
         * @param {Object} item
         */
        isHeader(item) {
            return item.legendType === 'header';
        },
    },
};
</script>

<style lang="scss">
.chart-legend {
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    margin: 0.25rem 0;

    .legend-item {
        margin: 0.2rem 1.5rem;

        .header {
            margin-top: 1em;
            font-weight: 700;
        }

        .row {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;

            > * {
                margin: 0 0.5em;
            }

            .legend-item-label {
                white-space: nowrap;
            }

            .value {
                flex: 0 0 auto;
                text-align: right;
                width: fit-content;
            }

            .visual-container {
                height: 1.25em;
                width: 2.5em;
                display: flex;
                align-items: center;
                justify-content: center;

                .line {
                    width: 2.5em;
                    height: 2px;
                    margin: 0;
                    border-width: 2px;

                    &.solid {
                        border-style: solid;
                    }

                    &.dashed {
                        border-style: dashed;
                    }
                }

                .circle {
                    width: 1em;
                    height: 1em;
                    box-sizing: border-box;
                    border-radius: 1.5em;

                    &.border {
                        border: 2px solid;
                    }
                }

                .color-box {
                    width: 2.5em;
                    height: 1.25em;
                    border-radius: 3px;
                    box-sizing: border-box;

                    &.border {
                        border: 2px solid;
                    }
                }
            }
        }

        &.transparent {
            .visual {
                opacity: 0.5;
            }

            .legend-item-label {
                text-decoration: line-through;
            }
        }
    }

    &.vertical {
        flex-direction: column;
        align-items: flex-end;
        flex-wrap: nowrap;

        .legend-item {
            width: 100%;
            margin: 0.2rem 0.25rem;

            .legend-item-label {
                white-space: unset;
                align-self: flex-start;
            }
        }
    }
}
</style>
